import { invert, isEmpty } from 'lodash';

import { escapeUnescapedQuotesAndBackslashes } from '@app/utils';

import { createBooleanOperatorQuery } from './parseMaskedAdvancedSearchQuery';
import {
  FieldQualifierValue,
  getCqlFieldQualifierValue,
} from './getCqlFieldQualifierValue';
import {
  consortiaFieldsToMaskMap,
  consortiaSearchOperatorMap,
  fieldsToMaskMap,
  FormValues,
  searchQualifierMaskMap,
} from '../constants';

const createAdvancedSearchQuery = ({
  searchRows,
  getQuery,
  shouldEscape = false,
}: {
  searchRows: FormValues;
  getQuery: (value: FieldQualifierValue) => string;
  shouldEscape?: boolean;
}) =>
  Object.values(searchRows).reduce((queryString, row) => {
    const { booleanOperator } = row;
    const shouldPrependBooleanOperator =
      !isEmpty(queryString) && booleanOperator !== undefined;
    const booleanOperatorQuery = shouldPrependBooleanOperator
      ? createBooleanOperatorQuery(booleanOperator)
      : '';

    const currentRowString = row.value
      ? `${booleanOperatorQuery}${getQuery({
          field: row.field,
          qualifier: row.qualifier,
          value: shouldEscape
            ? escapeUnescapedQuotesAndBackslashes(row.value)
            : row.value,
        })}`
      : '';

    return `${queryString}${currentRowString}`;
  }, '');

const getMaskedQuery = ({ field, qualifier, value }: FieldQualifierValue) =>
  `${fieldsToMaskMap[field]} ${searchQualifierMaskMap[qualifier]} "${value}"`;

const getConsortiaMaskedQuery = ({
  field,
  qualifier,
  value,
}: FieldQualifierValue) =>
  `${consortiaFieldsToMaskMap[field]}${
    invert(consortiaSearchOperatorMap)[qualifier]
  }"${value}"`;

export const createMaskedAdvancedSearchQuery = ({
  searchRows,
  isConsortiaMode = false,
}: {
  searchRows: FormValues;
  isConsortiaMode?: boolean;
}) =>
  createAdvancedSearchQuery({
    searchRows,
    getQuery: isConsortiaMode ? getConsortiaMaskedQuery : getMaskedQuery,
  });

export const createCqlAdvancedSearchQuery = ({
  searchRows,
  fieldMap,
}: {
  searchRows: FormValues;
  fieldMap: Record<string, string>;
}) =>
  createAdvancedSearchQuery({
    searchRows,
    getQuery: getCqlFieldQualifierValue(fieldMap),
    shouldEscape: true,
  });
